import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowRight, Wrench, Globe } from "lucide-react";
import ApplicationModal from "@/components/ApplicationModal";
import { useLang } from "@/i18n/LanguageContext";

export default function ArtistsPage() {
  const { lang } = useLang();
  const en = lang === "en";
  const [artists, setArtists] = useState([]);
  const [applyKind, setApplyKind] = useState(null); // "in-situ" | "dibond" | null
  const [stats, setStats] = useState({ "in-situ": { total: 0, this_week: 0 }, dibond: { total: 0, this_week: 0 } });
  const [aggregate, setAggregate] = useState({ totals: { "in-situ": 0, dibond: 0 }, "in-situ": [], dibond: [], invited: [] });

  useEffect(() => { api.get("/artists").then(r => setArtists(r.data)); }, []);
  useEffect(() => { api.get("/applications/stats").then(r => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/candidates/aggregate").then(r => setAggregate(r.data)).catch(() => {}); }, []);

  // Group artists by edition
  const promos = useMemo(() => {
    const byYear = { "2024": [], "2025": [] };
    for (const a of artists) {
      const y = a.edition || "2024";
      if (!byYear[y]) byYear[y] = [];
      byYear[y].push(a);
    }
    return byYear;
  }, [artists]);

  return (
    <main className="pt-32 pb-20" data-testid="artists-page">
      <div className="px-6 md:px-12 max-w-[1600px] mx-auto">
        <div className="meta-label text-cinabre">§ {en ? "Apply · Canal'Art 2026" : "Je postule · Canal'Art 2026"}</div>
        <h1 className="font-serif font-light text-6xl md:text-8xl leading-[0.9] tracking-tight mt-6 max-w-4xl">
          {en ? <>Join the <em className="italic">Thread</em>.</> : <>Rejoindre le <em className="italic">Fil</em>.</>}
        </h1>
        <p className="mt-6 max-w-2xl text-fg/75 leading-relaxed text-lg">
          {en ? <>Two ways to exhibit on the world's largest open-air art trail. Goal: <span className="text-cinabre">194 artists · 194 UNESCO countries</span> by 2030.</> : <>Deux façons d'exposer sur le plus grand parcours d'art à ciel ouvert du monde. Objectif <span className="text-cinabre">194 artistes · 194 pays UNESCO</span> à l'horizon 2030.</>}
        </p>

        {/* Deux formes de participation */}
        <section className="mt-16 md:mt-20" data-testid="participation-calls">
          <div className="meta-label text-cinabre">§ {en ? "Participation · Two ways to apply" : "Participation · Deux formes de candidature"}</div>
          <div className="mt-8 grid md:grid-cols-2 gap-6 md:gap-8">
            {/* Œuvres in situ — sur sélection */}
            <motion.article
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8 }}
              className="group border border-fg/15 p-8 md:p-10 hover:border-cinabre transition-colors bg-fg/[0.02]"
              data-testid="call-in-situ"
            >
              <div className="flex items-center gap-3">
                <Wrench size={16} className="text-cinabre" strokeWidth={1.5} />
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">§ {en ? "By selection" : "Sur sélection"}</div>
              </div>
              <h2 className="mt-4 font-serif font-light text-4xl md:text-5xl leading-[0.98] tracking-tight">
                {en ? <>Works <em className="italic">in situ</em>.</> : <>Œuvres <em className="italic">in situ</em>.</>}
              </h2>
              <p className="mt-5 font-serif text-lg leading-[1.55] text-fg/85">
                {en ? "Installations designed to converse with the landscape, the heritage and the canal's sites." : "Installations pensées pour dialoguer avec le paysage, le patrimoine et les sites du canal."}
              </p>
              <ul className="mt-6 space-y-2.5">
                {(en ? [
                  "Curatorial selection",
                  "Temporary, reversible projects",
                  "Technical support depending on the project",
                ] : [
                  "Sélection curatoriale",
                  "Projets temporaires et réversibles",
                  "Accompagnement technique selon les projets",
                ]).map((line) => (
                  <li key={line} className="flex items-start gap-3 font-serif text-base text-fg/80">
                    <span className="text-cinabre font-mono text-sm mt-1.5">§</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <ParticipationCounter total={stats["in-situ"].total} thisWeek={stats["in-situ"].this_week} label={en ? "application" : "candidature"} en={en} testid="counter-in-situ" />
              <button
                onClick={() => setApplyKind("in-situ")}
                className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-cinabre group-hover:gap-3 transition-all border-b border-cinabre/40 pb-1 hover:border-cinabre"
                data-testid="apply-in-situ"
              >
                {en ? "Apply · Works in situ" : "Postuler · Œuvres in situ"} <ArrowRight size={14} />
              </button>
            </motion.article>

            {/* Œuvres sur Dibond — appel ouvert */}
            <motion.article
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="group border border-fg/15 p-8 md:p-10 hover:border-cinabre transition-colors bg-fg/[0.02]"
              data-testid="call-dibond"
            >
              <div className="flex items-center gap-3">
                <Globe size={16} className="text-cinabre" strokeWidth={1.5} />
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">§ {en ? "Open call · International" : "Appel ouvert · International"}</div>
              </div>
              <h2 className="mt-4 font-serif font-light text-4xl md:text-5xl leading-[0.98] tracking-tight">
                {en ? <>Works on <em className="italic">Dibond</em>.</> : <>Œuvres sur <em className="italic">Dibond</em>.</>}
              </h2>
              <p className="mt-5 font-serif text-lg leading-[1.55] text-fg/85">
                {en ? "Artworks printed on Dibond panels and integrated into the trail along the canal." : "Œuvres imprimées sur support Dibond et intégrées au parcours le long du canal."}
              </p>
              <ul className="mt-6 space-y-2.5">
                {(en ? [
                  "Open to broad international participation",
                  "Professional printing",
                  "Visibility on the trail and on partner platforms",
                ] : [
                  "Accessible à une large participation internationale",
                  "Impression professionnelle",
                  "Visibilité dans le parcours et sur les plateformes partenaires",
                ]).map((line) => (
                  <li key={line} className="flex items-start gap-3 font-serif text-base text-fg/80">
                    <span className="text-cinabre font-mono text-sm mt-1.5">§</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <ParticipationCounter total={stats.dibond.total} thisWeek={stats.dibond.this_week} label={en ? "application" : "candidature"} en={en} testid="counter-dibond" />
              <button
                onClick={() => setApplyKind("dibond")}
                className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-cinabre group-hover:gap-3 transition-all border-b border-cinabre/40 pb-1 hover:border-cinabre"
                data-testid="apply-dibond"
              >
                {en ? "Apply · Dibond works" : "Candidater · Œuvres Dibond"} <ArrowRight size={14} />
              </button>
            </motion.article>
          </div>
        </section>

        {/* Timeline vivante — 3 promotions */}
        <div className="mt-24 md:mt-32 border-t border-fg/15 pt-16 md:pt-20" data-testid="promo-timeline">
          <div className="meta-label text-cinabre">§ {en ? "The Thread's cohorts · Living timeline" : "Les promotions du Fil · Timeline vivante"}</div>
          <h2 className="mt-4 font-serif font-light text-4xl md:text-6xl leading-[0.98] tracking-tight max-w-3xl">
            {en ? <>Three <em className="italic">cohorts</em>, one Thread.</> : <>Trois <em className="italic">promotions</em>, un même Fil.</>}
          </h2>
          <p className="mt-4 max-w-2xl text-fg/70 font-serif italic text-lg">
            {en ? "The trail's growth, year after year. Every new voice widens the geography." : "La croissance du parcours, année par année. Chaque nouvelle voix agrandit la géographie."}
          </p>
        </div>

        <PromoSection
          year="2026"
          en={en}
          headline={en ? "Edition in the making" : "Édition en construction"}
          intro={en ? "The great gathering of the 30th UNESCO anniversary. Applications are opening — every name that arrives writes what comes next." : "Le grand rassemblement du 30ᵉ anniversaire UNESCO. Les candidatures s'ouvrent — chaque nom qui arrive écrit la suite."}
          aggregate={aggregate}
          isCurrent
          onApplyInSitu={() => setApplyKind("in-situ")}
          onApplyDibond={() => setApplyKind("dibond")}
        />

        <PromoSection
          year="2025"
          en={en}
          headline={en ? "The pivotal year · CANAL'ART" : "L'année charnière · CANAL'ART"}
          intro={en ? "All exhibited in Castelnau-d'Estrétefonds — 42 works hung + Peggy Renaud's Wishing Tree. Some had 2 works, others, like the Club Photo de Fronton, 3. Official list on les-arteurs.org/canalart." : "Tous exposé·es à Castelnau-d'Estrétefonds — 42 œuvres accrochées + l'Arbre à Vœux de Peggy Renaud. Certains ont eu droit à 2 œuvres, d'autres, comme le Club Photo de Fronton, à 3. Liste officielle sur les-arteurs.org/canalart."}
          artists={promos["2025"] || []}
          isAlumni
        />

        <PromoSection
          year="2024"
          en={en}
          headline={en ? "The first step · JAIMECASTEL" : "La première marche · JAIMECASTEL"}
          intro={en ? "All exhibited in Castelnau-d'Estrétefonds — the first edition. 30 local and international artists, from Togo to Benin via Occitanie. Official list on les-arteurs.org/jaimecastel." : "Tous exposé·es à Castelnau-d'Estrétefonds — la première édition. 30 artistes locaux et internationaux, du Togo au Bénin en passant par l'Occitanie. Liste officielle sur les-arteurs.org/jaimecastel."}
          artists={promos["2024"] || []}
          isAlumni
        />
      </div>

      {applyKind && (
        <ApplicationModal
          open={!!applyKind}
          kind={applyKind}
          onClose={() => setApplyKind(null)}
        />
      )}
    </main>
  );
}


/** PromoSection — une promotion (2024, 2025 ou 2026-en-construction) */
function PromoSection({ year, headline, intro, artists = [], aggregate, isCurrent = false, isAlumni = false, onApplyInSitu, onApplyDibond, en = false }) {
  const totalCandidates = isCurrent ? (aggregate?.totals?.["in-situ"] || 0) + (aggregate?.totals?.dibond || 0) : 0;
  const count = isCurrent ? totalCandidates : artists.length;

  return (
    <section className="mt-16 md:mt-24" data-testid={`promo-${year}`}>
      {/* Header of the promo */}
      <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-end border-b border-fg/15 pb-6">
        <div className="md:col-span-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">§ Promotion</span>
            {isAlumni && (
              <span className="inline-block px-2 py-0.5 border border-cinabre text-cinabre font-mono text-[9px] tracking-[0.2em] uppercase" data-testid={`alumni-badge-${year}`}>
                Alumni
              </span>
            )}
            {isCurrent && (
              <span className="inline-block px-2 py-0.5 bg-cinabre text-bg font-mono text-[9px] tracking-[0.2em] uppercase animate-pulse-slow">
                {en ? "Open now" : "En cours"}
              </span>
            )}
          </div>
          <div className="font-serif font-light text-6xl md:text-7xl leading-[0.9] tracking-tight text-cinabre tabular-nums mt-2">
            {year}
          </div>
        </div>
        <div className="md:col-span-6">
          <h3 className="font-serif font-light text-3xl md:text-4xl leading-tight tracking-tight">
            {headline}
          </h3>
          <p className="mt-3 font-serif italic text-fg/70 text-base md:text-lg leading-snug max-w-xl">
            {intro}
          </p>
        </div>
        <div className="md:col-span-3 md:text-right">
          <div className="font-serif font-light text-3xl md:text-4xl tabular-nums leading-none">
            {count}
          </div>
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60 mt-1">
            {isCurrent
              ? (count > 0 ? (en ? `pre-registered` : `pré-inscrit${count > 1 ? "s" : ""}`) : (en ? "the call is open" : "l'appel est ouvert"))
              : (en ? `artist${count > 1 ? "s" : ""} on the Thread` : `artiste${count > 1 ? "s" : ""} sur le Fil`)}
          </div>
        </div>
      </div>

      {/* 2024/2025 · Alumni · photo grid */}
      {!isCurrent && artists.length > 0 && (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {artists.map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.5, delay: (i % 8) * 0.03 }}
            >
              <Link
                to={`/artistes/${a.slug}`}
                className="group relative overflow-hidden aspect-[3/4] border border-fg/15 block"
                data-testid={`artist-card-${a.slug}`}
              >
                <img src={a.image} alt={a.name} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-fg via-fg/30 to-transparent opacity-70" />
                <div className="absolute top-3 right-3 bg-bg/95 backdrop-blur px-2 py-1 font-mono text-[9px] tracking-[0.2em] uppercase text-fg/80">
                  {a.country}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 text-bg">
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-cinabre">{a.medium}</div>
                  <div className="font-serif text-2xl leading-tight mt-1">{a.name}</div>
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-bg/70 mt-1">→ {a.site}</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      {/* 2026 · pré-inscriptions par pays + hors concours */}
      {isCurrent && (
        <div className="mt-10 space-y-10">
          {/* Invités hors concours */}
          {aggregate?.invited?.length > 0 && (
            <div className="border-l-2 border-cinabre pl-6 md:pl-8 py-2" data-testid="invited-section">
              <div className="meta-label text-cinabre">§ {en ? "Out of competition · Guests of honour" : "Hors concours · Invité·e·s d'honneur"}</div>
              <div className="mt-3 space-y-2">
                {aggregate.invited.map((inv) => (
                  <div key={inv.name} className="flex items-baseline gap-3 flex-wrap font-serif">
                    <span className="text-2xl md:text-3xl font-light tracking-tight">{inv.name}</span>
                    <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
                      · {inv.country} · {inv.kind === "in-situ" ? "In situ" : "Dibond"}
                    </span>
                    {inv.note && (
                      <div className="w-full font-serif italic text-fg/70 text-sm md:text-base mt-1">
                        {inv.note}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {totalCandidates === 0 && aggregate?.invited?.length === 0 && (
            <div className="border border-dashed border-fg/25 p-10 md:p-14 grid md:grid-cols-12 gap-8 items-center" data-testid="promo-2026-empty">
              <div className="md:col-span-8">
                <div className="meta-label text-cinabre">§ {en ? "The call is open" : "L'appel est ouvert"}</div>
                <p className="mt-4 font-serif text-2xl md:text-3xl leading-[1.25] max-w-2xl">
                  {en ? <><em className="italic">No applications yet.</em> Be the first voice to write the 2026 cohort.</> : <><em className="italic">Aucune candidature pour l'instant.</em> Soyez la première voix à écrire la promo 2026.</>}
                </p>
              </div>
              <div className="md:col-span-4 flex md:justify-end gap-3 flex-wrap">
                <button onClick={onApplyInSitu} className="btn-editorial ghost">{en ? "Works in situ" : "Œuvres in situ"}</button>
                <button onClick={onApplyDibond} className="btn-editorial">{en ? "Dibond works" : "Œuvres Dibond"}</button>
              </div>
            </div>
          )}

          {/* Country aggregates · 2 columns : in-situ + dibond */}
          {(aggregate?.["in-situ"]?.length > 0 || aggregate?.dibond?.length > 0) && (
            <div className="grid md:grid-cols-2 gap-8 md:gap-12" data-testid="promo-2026-aggregate">
              <CountryColumn
                kindLabel={en ? "Works in situ" : "Œuvres in situ"}
                total={aggregate?.totals?.["in-situ"] || 0}
                rows={aggregate?.["in-situ"] || []}
                onCta={onApplyInSitu}
                ctaLabel={en ? "Apply · In situ" : "Postuler · In situ"}
                en={en}
              />
              <CountryColumn
                kindLabel={en ? "Dibond works" : "Œuvres Dibond"}
                total={aggregate?.totals?.dibond || 0}
                rows={aggregate?.dibond || []}
                onCta={onApplyDibond}
                ctaLabel={en ? "Apply · Dibond" : "Candidater · Dibond"}
                en={en}
              />
            </div>
          )}

          {/* Note info */}
          <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 border-t border-fg/10 pt-4">
            {en ? "§ Note · Verbal pre-agreements — artists must confirm by formal registration. Direct call on July 27." : "§ Note · Pré-accords verbaux — les artistes doivent confirmer par inscription formelle. Appel direct le 27 juillet."}
          </p>
        </div>
      )}
    </section>
  );
}


/** CountryColumn — colonne "Œuvres in situ / Dibond" avec agrégat par pays */
function CountryColumn({ kindLabel, total, rows, onCta, ctaLabel, en = false }) {
  return (
    <div className="border border-fg/15 p-6 md:p-8 bg-fg/[0.02]">
      <div className="flex items-baseline justify-between gap-4 border-b border-fg/10 pb-4">
        <div>
          <div className="meta-label text-cinabre">§ {kindLabel}</div>
          <div className="mt-1 font-serif italic text-fg/60 text-sm">{en ? "Pre-registered by country" : "Pré-inscrit·es par pays"}</div>
        </div>
        <div className="text-right">
          <div className="font-serif font-light text-3xl md:text-4xl tabular-nums leading-none text-cinabre">
            {total}
          </div>
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60 mt-1">
            {en ? "pre-registered" : `pré-inscrit${total > 1 ? "s" : ""}`}
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="mt-6 font-serif italic text-fg/60">{en ? "No pre-registrations yet." : "Aucune pré-inscription pour le moment."}</p>
      ) : (
        <ul className="mt-6 space-y-2.5">
          {rows.map((r) => (
            <li key={r.country} className="flex items-baseline justify-between border-b border-dashed border-fg/15 pb-2.5 last:border-b-0" data-testid={`country-row-${r.country}`}>
              <span className="font-serif text-lg md:text-xl notranslate" translate="no">{r.country}</span>
              <span className="flex items-baseline gap-2">
                <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
                  {en ? `application${r.count > 1 ? "s" : ""}` : `candidature${r.count > 1 ? "s" : ""}`}
                </span>
                <span className="font-serif text-2xl md:text-3xl tabular-nums text-cinabre">
                  {r.count}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}

      <button onClick={onCta} className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-cinabre border-b border-cinabre/40 pb-1 hover:border-cinabre">
        {ctaLabel} <ArrowRight size={13} />
      </button>
    </div>
  );
}


/** Compteur de candidatures — social proof éditorial */
function ParticipationCounter({ total, thisWeek, label = "candidature", testid, en = false }) {
  const plural = total > 1 ? "s" : "";
  const weekPlural = thisWeek > 1 ? "s" : "";
  return (
    <div className="mt-8 flex items-baseline gap-3 flex-wrap border-t border-fg/10 pt-5" data-testid={testid}>
      <span className="font-serif text-3xl md:text-4xl font-light leading-none text-cinabre tabular-nums">
        {total}
      </span>
      <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/70">
        {label}{plural} · {en ? "already on board" : "déjà à bord"}
      </span>
      {thisWeek > 0 && (
        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
          · {en ? <span className="text-cinabre">{thisWeek} new this week</span> : <>dont <span className="text-cinabre">{thisWeek} nouvelle{weekPlural} cette semaine</span></>}
        </span>
      )}
    </div>
  );
}
